<script setup lang="ts">
import { onClickOutside, useConfirmDialog } from '@vueuse/core'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import {
  computed,
  getCurrentInstance,
  nextTick,
  onUnmounted,
  ref,
  useAttrs,
  watch,
  type PropType,
} from 'vue'
import { overlayVisibilityCount, removeDocumentOverflowHidden } from '../../composables/useOverlay'

const props = defineProps({
  dismissible: {
    type: Boolean,
    required: false,
    default: false,
  },
  /** Transition name of the overlay element */
  transition: {
    type: String,
    required: false,
    default: 'fade',
  },
  /** Transition name of the content element */
  transitionType: {
    type: String,
    required: false,
    default: undefined,
  },
  /** Target container - CSS selector or DOM node */
  to: {
    type: [String, Object] as PropType<string | HTMLElement>,
    required: false,
    default: undefined,
  },
  modelValue: {
    type: Boolean,
    required: false,
    default: false,
  },
  focusTrap: {
    type: Boolean,
    required: false,
    default: true,
  },
})

interface Emit {
  (e: 'showing'): void
  (e: 'show'): void
  (e: 'shown'): void
  (e: 'hiding'): void
  (e: 'hide'): void
  (e: 'hidden'): void
  (e: 'cancel', data: unknown): void
  (e: 'confirm', data: unknown): void
  (e: 'reveal', data: unknown): void
  (e: 'update:modelValue', data: boolean): void
}

const emit = defineEmits(["showing", "show", "shown", "hiding", "hide", "hidden", "cancel", "confirm", "reveal", "update:modelValue"])

const { isRevealed, reveal, confirm, cancel, onReveal, onConfirm, onCancel } = useConfirmDialog()

const content = ref<HTMLDivElement>()

onClickOutside(content, () => {
  if (props.dismissible) {
    close()
  }
})

const disableTeleport = computed(() => !props.to)

// Methods/properties to be accessed via ref on the component instance
defineExpose({
  reveal,
  confirm: approve, // override to allow nested transitions
  cancel: close, // override to allow nested transitions
  isRevealed,
  onConfirm,
  onCancel,
})

const showMask = computed(() => isRevealed.value)
const showContent = ref(isRevealed.value)

watch(
  isRevealed,
  () => {
    showContent.value = isRevealed.value

    if (!isRevealed.value && overlayVisibilityCount.value > 0) {
      overlayVisibilityCount.value--
    } else if (isRevealed.value) {
      overlayVisibilityCount.value++
    }
  },
  {
    immediate: true,
  }
)

onUnmounted(() => {
  if (isRevealed.value && overlayVisibilityCount.value > 0) {
    overlayVisibilityCount.value--
  }
})

async function close(data?: unknown) {
  showContent.value = false

  await nextTick(() => {
    cancel(data)
  })
}

async function approve(data?: unknown) {
  showContent.value = false

  await nextTick(() => {
    confirm(data)
  })
}

// Methods/properties to be accessed via scoped slots
const slotProps = {
  reveal,
  confirm: approve,
  cancel: close /* override to allow nested transitions */,
}

// Transition events
const onShowing = () => {
  emit('showing')
}
const onShow = () => {
  emit('show')
}
const onShown = () => {
  if (isRevealed.value) {
    nextTick(() => {
      if (props.focusTrap) {
        activate()
      }
    })
  }
  emit('shown')
}
const onHiding = () => {
  emit('hiding')
}
const onHide = () => {
  emit('hide')
}
const onHidden = () => {
  removeDocumentOverflowHidden()
  emit('hidden')
}

// useConfirmDialog events
onCancel((data) => {
  deactivate()
  emit('cancel', data)
  if (props.modelValue) {
    emit('update:modelValue', false)
  }
})

onConfirm((data) => {
  deactivate()
  emit('confirm', data)
  if (props.modelValue) {
    emit('update:modelValue', false)
  }
})

onReveal((data) => {
  emit('reveal', data)
})

watch(
  () => props.modelValue,
  () => {
    if (props.modelValue) {
      reveal()
      emit('update:modelValue', true)
    }
  },
  {
    immediate: true,
  }
)

// Focus trap
const { activate, deactivate } = useFocusTrap(content, {
  fallbackFocus: content.value,
  allowOutsideClick: true,
})

// Workaround for data-v attributes not being applied to fragments and teleport
// https://github.com/vuejs/core/issues/2669
const instance = getCurrentInstance()
const scopeId = instance?.vnode.scopeId as string

const attrs = useAttrs()

const bindAttrs = computed(() => ({
  ...attrs,
  [scopeId]: '',
}))

defineOptions({
  inheritAttrs: false,
})
</script>

<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<template>
  <Teleport :disabled="disableTeleport" :to="to">
    <Transition
      :name="transition"
      appear
      @before-enter="onShowing"
      @enter="onShow"
      @after-enter="onShown"
      @before-leave="onHiding"
      @leave="onHide"
      @after-leave="onHidden"
    >
      <div
        v-if="showMask"
        class="oen-ui-overlay-mask"
        v-bind="bindAttrs"
        :class="{ 'overflow-hidden': !showContent }"
        @keydown.esc="close"
      >
        <Transition appear :name="transitionType">
          <div
            v-if="showContent"
            ref="content"
            role="dialog"
            aria-modal="true"
            class="oen-ui-overlay-content"
            :tabindex="-1"
          >
            <!-- Default slot -->
            <slot v-bind="slotProps" />
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.oen-ui-overlay-mask {
  &::-webkit-scrollbar-track {
    background-color: #b2b7c1;
    /* otherwise we could see several scrollbars (behind the mask) */
  }

  &::-webkit-scrollbar-thumb {
    border-color: #b2b7c1;
  }

  @apply fixed right-0 left-0 z-50;
  @apply overflow-y-auto;
  @apply flex items-center justify-center;
  @apply bg-tertiary-text;
  top: env(safe-area-inset-top);
  bottom: env(safe-area-inset-bottom);

  &.fade-enter-active,
  &.fade-leave-active {
    @apply overflow-hidden;
  }

  .oen-ui-overlay-content {
    @apply m-auto;
  }
}
</style>
